/**
 * THE originating-edge transport for a browser flow client that owns its OWN credential and its OWN failure shape.
 *
 * WHY IT EXISTS BESIDE `fetchAdminApi` RATHER THAN INSIDE IT. `fetchAdminApi` (`./admin-api-utils.ts`) is the ADMIN
 * client: it resolves a Firebase bearer, retries once on 401, stamps dev user headers, and THROWS `AdminAPIError` on any
 * non-ok response, discarding the body. Every browser-edge flow client this leaf serves contradicts at least one of
 * those: wishlists and the chats viewer carry a bridge-read token (`readBridgeIdToken`) precisely BECAUSE a second
 * Firebase instance breaks the shell's session, the chats upload accepts a guest session token, the explore feed and
 * the title probes are public, and every one of them READS THE ENVELOPE ON A NON-OK RESPONSE to build its own failure
 * value or telemetry line. Routing them through the admin client would have rewritten seven public contracts to buy one
 * header. This leaf buys the header alone.
 *
 * WHAT IT ADDS OVER A BARE `fetch(...)`, and it is exactly two things: the ORIGINATING-EDGE `traceparent`
 * ({@link stampRequestTraceparent}, the one stamp `prepareHeaders` also composes) so a worker-side flow run can JOIN the
 * page interaction that provoked it, and the shared timeout of `httpFetch`. Nothing else is decided here -- no
 * credential, no envelope, no status policy. Transport/timeout failure THROWS, which is what a bare `fetch` already
 * did, so a caller's existing `try`/`catch` -- or its absence -- keeps working unchanged.
 *
 * WHY THIS HOME. Same reason `mfe-flow-fetch-spine.ts` gives for its own: the leaf value-imports `httpFetchOrThrow`
 * (`tools/http/fetch`), so a `tools/http/` home would be a tools-to-tools import that `QUAL-NO-CROSS-TOOL-IMPORTS-001`
 * bans with no same-directory exemption, and `utils/` may not import `tools/` at all. `components/lib/api/` is the
 * DI/transport subtree `QUAL-COMP-PURE-001` exempts, and it is where the trace stamp's other consumer already lives.
 *
 * Paired forbid-rule: `QUAL-FLOW-ROUTE-RAW-FETCH-001` re-arms on any bare `fetch(` dialling an MFE flow route.
 */

import { API_TIMEOUTS } from '@federa/const/timing';
import { STANDARD_HEADERS } from '@federa/const/http-header-names';
import { newRequestTraceparent } from '@federa/utils/telemetry/page-load-traceparent';
import { httpFetchOrThrow } from '@federa/tools/http/fetch';

/**
 * The ONE originating-edge `traceparent` stamp: a fresh span id on this realm's page-load trace, set only when the
 * caller did not supply one and only when a randomness source exists (an un-traced request is correct, a malformed
 * header is not).
 *
 * `prepareHeaders` (`./fetch-helpers.ts`) composes this rather than re-spelling it, so the whole browser surface --
 * admin client and un-authed edge client alike -- mints the header on one decision. A caller-supplied `traceparent` is
 * left alone, so a future in-page tracer can own the header without fighting either consumer.
 *
 * @runtime-derived-ssot-helper: composes `newRequestTraceparent` (`utils/telemetry/page-load-traceparent.ts`) under the
 * `STANDARD_HEADERS.TRACEPARENT` name SSOT, so no caller names the header or the grammar.
 */
export function stampRequestTraceparent(existingHeaders?: HeadersInit): Headers {
	const headers = new Headers(existingHeaders);
	const traceparent = newRequestTraceparent();
	if (traceparent !== '' && !headers.has(STANDARD_HEADERS.TRACEPARENT)) {
		headers.set(STANDARD_HEADERS.TRACEPARENT, traceparent);
	}
	return headers;
}

/**
 * Bare-`fetch` semantics plus the originating-edge trace header and a timeout.
 *
 * The signature mirrors `fetchAdminApi` deliberately -- `(url, options-with-timeout, signal)` -- so the two browser
 * transports read the same at a call site and the latency decision stays INLINE at each service, which is the same
 * reason `mfe-flow-fetch-spine.ts` takes a thunk rather than a URL.
 *
 * Returns the `Response` at ANY status: the caller owns its own non-ok policy, because every one of them differs (an
 * envelope failure value, a telemetry line naming the flow, a soft empty page). Only transport and timeout failures
 * throw, matching what a bare `fetch` did before.
 */
export function fetchTracedEdge(url: string, options: RequestInit & { timeout?: number } = {}, signal?: AbortSignal): Promise<Response> {
	const { timeout = API_TIMEOUTS.standard, ...init } = options;
	return httpFetchOrThrow(url, { init: { ...init, headers: stampRequestTraceparent(init.headers) }, timeoutMs: timeout, signal });
}
